import { useState } from 'react'
import { generatePath, useHistory, useParams } from 'react-router-dom'
import { brandVoicePath } from 'app/navigation/paths'
import cx from 'classnames'

import Button from 'common/components/button'
import Footer from 'common/components/layout/Footer'
import PageContainer from 'common/components/PageContainer'
import PageHeader from 'common/components/PageHeader'
import PageTitle from 'common/components/PageTitle'
import Scrollable from 'common/components/scrollable'
import Spinner from 'common/components/spinner'
import Widget from 'common/components/Widget'
import { useDocumentTitle } from 'common/hooks/custom'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { Edit as EditIcon, Magic2 as MagicHatIcon } from 'common/icons'

import useGetBrandVoiceDetailsQuery from '../../api/queries/useGetBrandVoiceDetailsQuery'
import {
  getTonesWithColorInfo,
  setGenerateTone,
} from '../../store/brandVoiceSlice'
import EditTones from '../EditTones'
import GenerateTones from '../GenerateTones'

import ToneCard from './components/ToneCard'

const ToneOfVoice = () => {
  useDocumentTitle('Tone of Voice | Phrasee')

  const dispatch = useAppDispatch()
  const history = useHistory()
  const { accountId, brandVoiceId } =
    useParams<{ accountId: string; brandVoiceId: string }>()

  const [isEditingTones, setIsEditingTones] = useState<boolean>(false)

  const { data: brandVoice } = useGetBrandVoiceDetailsQuery(
    accountId,
    brandVoiceId
  )

  const regionId = useAppSelector((state) => state.styleGuide.regionId)
  if (!regionId) {
    history.push(
      generatePath(brandVoicePath, {
        accountId,
      })
    )
  }

  const { isLoading, isGenerateTone } = useAppSelector(
    (state) => state.brandVoice
  )

  const brandVoiceTones = getTonesWithColorInfo(brandVoice)
  const toneCount = brandVoiceTones.length
  const assignedToneIds = brandVoiceTones?.map((tone) => tone.id)

  return (
    <>
      {toneCount === 0 || isGenerateTone ? (
        <GenerateTones />
      ) : (
        <>
          {isEditingTones ? (
            <EditTones
              assignedToneIds={assignedToneIds}
              onDoneEditing={() => {
                setIsEditingTones(false)
              }}
            />
          ) : (
            <Scrollable
              isFullHeight={false}
              content={
                <PageContainer className="max-w-318 sm:mx-auto px-6 pt-12 lg:pt-8">
                  <PageHeader>
                    <div
                      data-cy="header"
                      className="flex justify-between items-center w-full"
                    >
                      <div>
                        <PageTitle title="Tone of Voice" />
                        <div className="text-coolGray-400">
                          This lets Phrasee know what we should or should not
                          use in the generation of your content.
                        </div>
                      </div>
                      <div className=" text-right w-140">
                        <Button
                          data-cy="edit-tone-button"
                          data-testid="edit-tone-button"
                          aria-label="Edit"
                          ghost={true}
                          onClick={() => {
                            setIsEditingTones(true)
                          }}
                          className="text-base"
                          prefixIcon={<EditIcon isDefaultColor={false} />}
                        >
                          Edit
                        </Button>
                        <Button
                          data-cy="regenerate-tone-button"
                          data-testid="regenerate-tone-button"
                          aria-label="Regenerate tone"
                          ghost={true}
                          onClick={() => {
                            dispatch(setGenerateTone(true))
                          }}
                          className="text-base ml-4"
                          prefixIcon={<MagicHatIcon isDefaultColor={false} />}
                        >
                          Re-generate brand voice
                        </Button>
                      </div>
                    </div>
                  </PageHeader>
                  <Widget type="basic" className="mt-6">
                    <Spinner isSpinning={isLoading}>
                      <div
                        className={cx('grid gap-4 grid-cols-1', {
                          'sm:grid-cols-2': toneCount === 2 || toneCount === 4,
                          'sm:grid-cols-3': toneCount === 3,
                          'sm:grid-cols-6': toneCount === 5,
                        })}
                      >
                        {brandVoiceTones?.map((tone) => (
                          <ToneCard
                            className={
                              toneCount === 5
                                ? cx({
                                    'col-span-2': tone.order < 3,
                                    'col-span-3': tone.order >= 3,
                                  })
                                : undefined
                            }
                            isSelected={true}
                            key={tone.id}
                            tone={tone}
                          />
                        ))}
                      </div>
                    </Spinner>
                  </Widget>

                  <Footer />
                </PageContainer>
              }
            />
          )}
        </>
      )}
    </>
  )
}

export default ToneOfVoice
